import React, { useState, useEffect } from 'react'
import FyMonthlyBarChart from '../../components/Charts/FyMonthlyBarChart'
import { currentFyLabel } from '../../utils/financialYearMonths'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import supportDashboardService from '../../services/supportDashboardService'
import { useModuleView } from '../../hooks/useModuleView'
import ModuleOverviewCard from '../../components/Module/ModuleOverviewCard'
import ModuleViewBackLink from '../../components/Module/ModuleViewBackLink'
import { 
  Users,
  Activity,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Brain,
  DollarSign,
  Target,
  AlertTriangle,
  CheckCircle,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  MessageSquare,
  FileText,
  Briefcase,
  Headphones,
  HelpCircle,
  MessageSquareText,
  Phone,
  PieChart
} from 'lucide-react'
// Consolidated CSS is imported globally'

const Support = () => {
  const navigate = useNavigate()
  const moduleView = useModuleView()
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const response = await supportDashboardService.getDashboardStats()
      if (response.success) {
        setDashboardData(response.data)
      } else if (!response._errorHandled) {
        toast.error(response.message || 'Failed to load dashboard data')
      }
    } catch (error) {
      console.error('Error loading support dashboard data:', error)
      if (!error._errorHandled) {
        if (error.response) {
          toast.error(error.response.data?.message || 'Failed to load dashboard data')
        } else if (!error.request) {
          toast.error('Failed to load dashboard data')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now'
    const date = new Date(timestamp)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)
    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
    return date.toLocaleDateString()
  }

  const formatHours = (hours) => {
    if (hours == null) return '—'
    if (hours < 1) return `${Math.round(hours * 60)}m`
    return `${hours}h`
  }

  const handleSupportTicketsClick = () => navigate('/support/support-tickets')

  return (
    <div className="sales-dashboard">
      {/* Page Header */}
      <div className="page-header" style={{textAlign: 'left'}}>
        <div className="page-title" style={{textAlign: 'left', alignItems: 'flex-start'}}>
          <h1 style={{textAlign: 'left', margin: '0 0 4px 0'}}>Support</h1>
          <p style={{textAlign: 'left', margin: 0}}>Manage customer support tickets and service requests</p>
        </div>
      </div>

      {/* Tab Content */}
      {moduleView === 'overview' && (
        <div className="tab-content">
          <ModuleViewBackLink onClick={() => navigate('/support')} />

          {/* Key Metrics Grid */}
          <div className="dashboard-metrics-grid">
            {/* Support Metrics */}
            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon">
                  <MessageSquareText size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.openTickets?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.openTickets?.change >= 0 ? '+' : ''}{dashboardData?.openTickets?.change ?? 0}</span>
                </div>
              </div>
              <div className="metric-value">{loading ? '...' : (dashboardData?.openTickets?.count ?? 0)}</div>
              <div className="metric-title">Open Tickets</div>
              <div className="metric-subtitle">Currently open</div>
            </div>

            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon">
                  <Clock size={16} />
                </div>
                <div className="metric-trend neutral">
                  <Clock size={12} />
                  <span>FY</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : formatHours(dashboardData?.avgResolutionHours?.hours)}
              </div>
              <div className="metric-title">Avg Resolution Time</div>
              <div className="metric-subtitle">Resolved tickets</div>
            </div>

            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon">
                  <AlertTriangle size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.highPriority?.count > 0 ? 'negative' : 'positive'}`}>
                  <TrendingDown size={12} />
                  <span>{dashboardData?.highPriority?.status || 'CLEAR'}</span>
                </div>
              </div>
              <div className="metric-value">{loading ? '...' : (dashboardData?.highPriority?.count ?? 0)}</div>
              <div className="metric-title">High Priority</div>
              <div className="metric-subtitle">Open high / critical</div>
            </div>

            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon">
                  <CheckCircle size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.resolutionRate?.status === 'EXCELLENT' ? 'positive' : dashboardData?.resolutionRate?.status === 'NO_DATA' ? 'neutral' : 'negative'}`}>
                  <CheckCircle size={12} />
                  <span>{(dashboardData?.resolutionRate?.status || 'NO DATA').replace(/_/g, ' ')}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading
                  ? '...'
                  : dashboardData?.resolutionRate?.status === 'NO_DATA'
                    ? '—'
                    : `${dashboardData?.resolutionRate?.rate ?? 0}%`}
              </div>
              <div className="metric-title">Resolution Rate</div>
              <div className="metric-subtitle">Resolved / created (FY)</div>
            </div>
          </div>

          {/* Action Items & Alerts */}
          <div className="dashboard-action-section">
            <div className="dashboard-action-items">
              <div className="section-header">
                <h3>Support Action Items</h3>
                <span className="item-count">{dashboardData?.actionItems?.length ?? 0} items</span>
              </div>
              <div className="action-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading action items...</div>
                ) : dashboardData?.actionItems?.length > 0 ? (
                  dashboardData.actionItems.map((item, index) => {
                    const Icon = item.type === 'sla' ? Clock : AlertTriangle
                    return (
                      <div key={index} className={`action-item ${item.priority}`}>
                        <div className="action-icon"><Icon size={16} /></div>
                        <div className="action-content">
                          <div className="action-title">{item.title}</div>
                          <div className="action-description">{item.description}</div>
                        </div>
                        <div className="action-time">{formatTimeAgo(item.timestamp)}</div>
                      </div>
                    )
                  })
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No action items</div>
                )}
              </div>
            </div>

            <div className="dashboard-team-activity">
              <div className="section-header">
                <h3>Support Team Activity</h3>
                <span className="activity-count">{dashboardData?.teamActivity?.length ?? 0} updates</span>
              </div>
              <div className="activity-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading team activity...</div>
                ) : dashboardData?.teamActivity?.length > 0 ? (
                  dashboardData.teamActivity.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-avatar">{activity.employeeInitials || 'UN'}</div>
                      <div className="activity-content">
                        <div className="activity-text">
                          <strong>{activity.employeeName || 'Unknown'}</strong> {activity.activity}
                        </div>
                        <div className="activity-time">{formatTimeAgo(activity.timestamp)}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No recent activity</div>
                )}
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="dashboard-charts-section">
            <div className="dashboard-chart-card revenue-chart">
              <div className="chart-header">
                <h3>Support Ticket Trend</h3>
                <p>{dashboardData?.financialYear ? `FY ${dashboardData.financialYear}` : `FY ${currentFyLabel()}`}</p>
              </div>
              <div className="chart-content">
                <FyMonthlyBarChart
                  series={dashboardData?.ticketTrend}
                  valueKey="revenue"
                  formatTooltip={(v) => `${v} ticket${v === 1 ? '' : 's'}`}
                  emptyMessage="No tickets this financial year"
                />
              </div>
            </div>

            <div className="dashboard-chart-card customer-health">
              <div className="chart-header">
                <h3>Ticket Priority Distribution</h3>
                <p>Open tickets</p>
              </div>
              <div className="chart-content">
                <div className="health-distribution">
                  {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading...</div>
                  ) : dashboardData?.priorityDistribution?.some((r) => r.count > 0) ? (
                    dashboardData.priorityDistribution.filter((r) => r.count > 0).map((row, index) => {
                      const classes = ['excellent', 'good', 'at-risk', 'churned']
                      const cls = classes[index % classes.length]
                      return (
                        <div key={row.name || index} className={`health-segment ${cls}`}>
                          <div className="segment-bar" style={{ width: `${Math.max(row.percent || 0, row.count > 0 ? 4 : 0)}%` }} />
                          <div className="segment-info">
                            <span className="segment-label">{row.name}</span>
                            <span className="segment-count">{row.count}</span>
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No open tickets</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'masters' && (
        <div className="tab-content">
          <div className="support-masters-content">
            <div className="support-masters-header">
              <h2>Support Masters</h2>
              <p>Manage your support master data and configurations</p>
            </div>
            <div className="support-masters-grid">
              <div className="support-master-card">
                <div className="support-master-icon">
                  <Users size={24} />
                </div>
                <div className="support-master-content">
                  <h3>Support Agents</h3>
                  <p>Support team members and their specializations</p>
                  <div className="support-master-count">—</div>
                </div>
              </div>
              <div className="support-master-card">
                <div className="support-master-icon">
                  <Target size={24} />
                </div>
                <div className="support-master-content">
                  <h3>Categories</h3>
                  <p>Support ticket categories and classifications</p>
                  <div className="support-master-count">—</div>
                </div>
              </div>
              <div className="support-master-card">
                <div className="support-master-icon">
                  <HelpCircle size={24} />
                </div>
                <div className="support-master-content">
                  <h3>Knowledge Base</h3>
                  <p>FAQ articles and help documentation</p>
                  <div className="support-master-count">—</div>
                </div>
              </div>
              <div className="support-master-card">
                <div className="support-master-icon">
                  <MessageSquareText size={24} />
                </div>
                <div className="support-master-content">
                  <h3>Escalation Rules</h3>
                  <p>Ticket escalation and routing rules</p>
                  <div className="support-master-count">—</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'transactions' && (
        <div className="tab-content">
          <div className="support-transactions-content">
            <div className="support-transactions-header">
              <h2>Support Transactions</h2>
              <p>Track and manage all support activities and tickets</p>
            </div>
            <div className="support-transactions-grid">
              <ModuleOverviewCard
                prefix="support-transaction"
                moduleName="Support"
                showArrow={false}
                onClick={() => navigate('/support?view=overview')}
              />
              <div className="support-transaction-card" onClick={handleSupportTicketsClick} style={{ cursor: 'pointer' }}>
                <div className="support-transaction-icon">
                  <MessageSquareText size={24} />
                </div>
                <div className="support-transaction-content">
                  <h3>Support Tickets</h3>
                  <p>Customer support requests and issues</p>
                  <div className="support-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.open ?? 0} Open`}
                  </div>
                </div>
              </div>
              <div className="support-transaction-card">
                <div className="support-transaction-icon">
                  <Phone size={24} />
                </div>
                <div className="support-transaction-content">
                  <h3>Phone Calls</h3>
                  <p>Incoming and outgoing support calls</p>
                  <div className="support-transaction-count">—</div>
                </div>
              </div>
              <div className="support-transaction-card">
                <div className="support-transaction-icon">
                  <MessageSquare size={24} />
                </div>
                <div className="support-transaction-content">
                  <h3>Live Chats</h3>
                  <p>Real-time chat support sessions</p>
                  <div className="support-transaction-count">—</div>
                </div>
              </div>
              <div className="support-transaction-card">
                <div className="support-transaction-icon">
                  <CheckCircle size={24} />
                </div>
                <div className="support-transaction-content">
                  <h3>Resolutions</h3>
                  <p>Completed and resolved support cases</p>
                  <div className="support-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.resolved ?? 0} Resolved`}
                  </div>
                </div>
              </div>
              <div
                className="support-transaction-card"
                onClick={() => navigate('/support?tab=reports')}
                style={{ cursor: 'pointer' }}
              >
                <div className="support-transaction-icon">
                  <PieChart size={24} />
                </div>
                <div className="support-transaction-content">
                  <h3>Reports</h3>
                  <p>Support analytics and performance reporting</p>
                  <div className="support-transaction-count">—</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'reports' && (
        <div className="tab-content">
          <ModuleViewBackLink
            onClick={() => navigate('/support?tab=transactions')}
            label="Back to transactions"
          />
          <div className="support-reports-content">
            <div className="support-reports-header">
              <h2>Support Reports</h2>
              <p>Comprehensive support analytics and reporting</p>
            </div>
            <div className="support-reports-grid">
              <div className="support-report-card">
                <div className="support-report-icon">
                  <BarChart3 size={24} />
                </div>
                <div className="support-report-content">
                  <h3>Performance Reports</h3>
                  <p>Support team performance and metrics</p>
                  <div className="support-report-status">Updated Today</div>
                </div>
              </div>
              <div className="support-report-card">
                <div className="support-report-icon">
                  <Users size={24} />
                </div>
                <div className="support-report-content">
                  <h3>Customer Satisfaction</h3>
                  <p>Customer feedback and satisfaction scores</p>
                  <div className="support-report-status">Updated Today</div>
                </div>
              </div>
              <div className="support-report-card">
                <div className="support-report-icon">
                  <Clock size={24} />
                </div>
                <div className="support-report-content">
                  <h3>Response Time Reports</h3>
                  <p>Average response and resolution times</p>
                  <div className="support-report-status">Updated Today</div>
                </div>
              </div>
              <div className="support-report-card">
                <div className="support-report-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="support-report-content">
                  <h3>Trend Analysis</h3>
                  <p>Support trends and pattern analysis</p>
                  <div className="support-report-status">Updated Today</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Support
